import { spring, interpolate } from 'remotion';
import { THEME, getScale, getLayoutMode } from '../../themes/brand';
import { getIcon } from './iconMap';
import { Zap } from 'lucide-react';
import type { BrollComponentProps } from './registry';

interface Feature {
  icon?: string;
  label?: string;
  name?: string;  // alias for label (storyboard compat)
  desc?: string;
  description?: string;  // alias for desc (storyboard compat)
}

interface FeatureProps {
  title?: string;
  icon?: string;
  features?: Feature[];
  layout?: 'grid' | 'vertical';
}

export const TemplateFeature: React.FC<BrollComponentProps> = ({
  frame, fps, width, height, durationInFrames, props,
}) => {
  const s = getScale(height);
  const mode = getLayoutMode(height);
  const data = (props || {}) as FeatureProps;
  const dur = durationInFrames;

  const title = data.title || 'Features';
  const TitleIcon = getIcon(data.icon) || Zap;
  const features: Feature[] = data.features || [
    { icon: 'Zap', label: 'Fast', desc: 'Lightning quick results' },
    { icon: 'Target', label: 'Precise', desc: 'Accurate every time' },
    { icon: 'Shield', label: 'Secure', desc: 'Enterprise-grade security' },
  ];
  const layoutStyle = data.layout || 'grid';

  const maxFeatures = mode === 'compact' ? 4 : 6;
  const visibleFeatures = features.slice(0, maxFeatures);
  const cols = layoutStyle === 'vertical' ? 1 : (visibleFeatures.length <= 3 ? visibleFeatures.length : 2);
  const featureStaggerGap = Math.max(3, Math.floor((dur * 0.5) / visibleFeatures.length));
  const featureStaggerBase = Math.round(dur * 0.1);
  const titleEntrance = spring({ frame, fps, config: { damping: 16 }, durationInFrames: Math.max(10, Math.round(dur * 0.15)) });

  return (
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      backgroundColor: THEME.colors.bg, fontFamily: THEME.font.family,
      padding: THEME.spacing.xl * s,
    }}>
      {/* Title with icon-in-pill */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: THEME.spacing.md * s,
        marginBottom: THEME.spacing.xxl * s,
        opacity: titleEntrance,
        transform: `translateY(${interpolate(titleEntrance, [0, 1], [20, 0])}px)`,
      }}>
        <div style={{
          width: 44 * s, height: 44 * s,
          backgroundColor: THEME.colors.bgSecondary,
          borderRadius: THEME.radius.md * s,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid ${THEME.colors.border}`,
        }}>
          <TitleIcon size={22 * s} color={THEME.colors.accent} />
        </div>
        <div style={{
          color: THEME.colors.text, fontSize: THEME.font.title * s,
          fontWeight: THEME.font.weight.bold, letterSpacing: 2 * s,
        }}>
          {title}
        </div>
      </div>

      {/* Feature cards */}
      <div style={{
        display: 'flex', flexWrap: 'wrap',
        gap: THEME.spacing.lg * s, justifyContent: 'center',
        maxWidth: cols === 1 ? 700 * s : '100%',
      }}>
        {visibleFeatures.map((feat, i) => {
          const delay = featureStaggerBase + i * featureStaggerGap;
          const entrance = spring({
            frame: Math.max(0, frame - delay), fps,
            config: { damping: 12, stiffness: 90 },
          });

          const featLabel = feat.label || feat.name || '';
          const featDesc = feat.desc || feat.description;
          const FeatIcon = getIcon(feat.icon) || Zap;
          const iconColor = THEME.colors.chart[i % THEME.colors.chart.length];

          const cardWidth = cols === 1
            ? '100%'
            : `${(width - THEME.spacing.xl * s * 2 - THEME.spacing.lg * s * (cols - 1)) / cols}px`;

          return (
            <div key={i} style={{
              width: cardWidth,
              backgroundColor: THEME.colors.bgCard,
              borderRadius: THEME.radius.lg * s,
              padding: `${THEME.spacing.lg * s}px`,
              border: `1px solid ${THEME.colors.border}`,
              opacity: entrance,
              transform: `translateY(${interpolate(entrance, [0, 1], [40, 0])}px) scale(${interpolate(entrance, [0, 1], [0.9, 1])})`,
              display: 'flex', alignItems: 'center', gap: THEME.spacing.lg * s,
            }}>
              {/* Icon-in-pill */}
              <div style={{
                width: 48 * s, height: 48 * s, flexShrink: 0,
                backgroundColor: THEME.colors.bgSecondary,
                borderRadius: THEME.radius.md * s,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `1px solid ${THEME.colors.border}`,
              }}>
                <FeatIcon size={24 * s} color={iconColor} />
              </div>

              <div>
                <div style={{
                  color: THEME.colors.text,
                  fontSize: THEME.font.subtitle * s * 0.9,
                  fontWeight: THEME.font.weight.semibold,
                  marginBottom: featDesc ? THEME.spacing.xs * s : 0,
                }}>
                  {featLabel}
                </div>
                {featDesc && (
                  <div style={{
                    color: THEME.colors.textMuted,
                    fontSize: THEME.font.label * s * 1.1,
                    lineHeight: 1.4,
                  }}>
                    {featDesc}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
